/**
 * Project Details Page with Tabs
 * 
 * Main page for viewing project details with tabs for:
 * - Sources list
 * - Project info
 * - Action history (operator actions for this project)
 * - Parsing history (Yandex parsing for this project)
 * 
 * * Tab selection persists via URL parameters
 */

import { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Tabs, Card, Modal, message } from 'antd'
import api from '@/services/api'
import {
  UnorderedListOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  CloudUploadOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import SourcesListTab from '@/components/ProjectTabs/SourcesListTab'
import ActionHistoryTab from '@/components/ProjectTabs/ActionHistoryTab'
import ParsingHistoryTab from '@/components/ProjectTabs/ParsingHistoryTab'
import VDLUploadHistoryTab from '@/components/ProjectTabs/VDLUploadHistoryTab'
import PhoneExtractionHistoryTab from '@/components/ProjectTabs/PhoneExtractionHistoryTab'
import ProjectInfoTab from '@/components/ProjectTabs/ProjectInfoTab'
import ProjectHeader from '@/components/ProjectHeader'

function ProjectDetailsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const sourcesTabRef = useRef<any>(null)
  const [project, setProject] = useState<any>(null)

  // * Get active tab from URL or default to 'sources'
  const activeTab = searchParams.get('tab') || 'sources'

  // * Load project data
  useEffect(() => {
    const loadProject = async () => {
      if (projectId) {
        try {
          const data = await api.getProject(Number(projectId))
          setProject(data)
        } catch (error) {
          console.error('Failed to load project:', error)
        }
      }
    }
    loadProject()
  }, [projectId])

  // * Initialize tab from localStorage on first visit if no URL param exists
  useEffect(() => {
    if (!searchParams.has('tab')) {
      const savedTab = localStorage.getItem(`project-${projectId}-active-tab`)
      if (savedTab && ['sources', 'info', 'history', 'parsing', 'vdl-upload', 'phone-extraction'].includes(savedTab)) {
        setSearchParams({ tab: savedTab }, { replace: true })
      } else {
        setSearchParams({ tab: 'sources' }, { replace: true })
      }
    }
  }, [projectId, searchParams, setSearchParams])

  if (!projectId) {
    return null
  }

  const handleTabChange = (key: string) => {
    // * Update URL parameter
    setSearchParams({ tab: key })
    // * Save to localStorage for persistence
    localStorage.setItem(`project-${projectId}-active-tab`, key)
  }

  const handleSync = () => {
    // * Trigger sync in SourcesListTab
    if (sourcesTabRef.current?.triggerSync) {
      sourcesTabRef.current.triggerSync()
    }
  }

  const handleGenerateConfig = () => {
    // * Trigger config generation in SourcesListTab
    if (sourcesTabRef.current?.triggerGenerateConfig) {
      sourcesTabRef.current.triggerGenerateConfig()
    }
  }

  const handleReanalyze = () => {
    if (!project) return
    Modal.confirm({
      title: 'Переанализировать все домены',
      content: `Все проанализированные домены проекта "${project.project_name}" будут сброшены и отправлены на повторную оценку нейронкой с текущими настройками AI-конфига. Это может занять время.`,
      okText: 'Переанализировать',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await api.reanalyzeProject(project.crm_id)
          message.success(`Переанализ запущен: ${result.sources_reset} доменов, ${result.chunks} батчей`)
        } catch (error: any) {
          const detail = error.response?.data?.detail || 'Ошибка запуска переанализа'
          message.error(detail)
        }
      },
    })
  }

  const tabItems = [
    {
      key: 'sources',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <UnorderedListOutlined style={{ fontSize: '16px' }} />
          <span>Источники</span>
        </span>
      ),
      children: <SourcesListTab ref={sourcesTabRef} projectId={Number(projectId)} />,
    },
    {
      key: 'info',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <InfoCircleOutlined style={{ fontSize: '16px' }} />
          <span>Информация</span>
        </span>
      ),
      children: <ProjectInfoTab projectId={Number(projectId)} />,
    },
    {
      key: 'history',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <HistoryOutlined style={{ fontSize: '16px' }} />
          <span>История действий</span>
        </span>
      ),
      children: <ActionHistoryTab projectId={Number(projectId)} />,
    },
    {
      key: 'parsing',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <RobotOutlined style={{ fontSize: '16px' }} />
          <span>История парсинга</span>
        </span>
      ),
      children: (
        <ParsingHistoryTab 
          projectId={Number(projectId)} 
          projectCrmId={project?.crm_id}
          projectName={project?.project_name}
        />
      ),
    },
    {
      key: 'vdl-upload',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <CloudUploadOutlined style={{ fontSize: '16px' }} />
          <span>История загрузки VDL</span>
        </span>
      ),
      children: <VDLUploadHistoryTab projectId={Number(projectId)} />,
    },
    {
      key: 'phone-extraction',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }}>
          <PhoneOutlined style={{ fontSize: '16px' }} />
          <span>Сбор телефонов</span>
        </span>
      ),
      children: <PhoneExtractionHistoryTab projectId={Number(projectId)} />,
    },
  ]

  return (
    <div style={{ padding: '20px 20px 24px 20px' }}>
      {/* * Unified Project Header */}
      <ProjectHeader
        projectId={Number(projectId)}
        onSync={handleSync}
        onGenerateConfig={handleGenerateConfig}
        onReanalyze={handleReanalyze}
      />

      {/* * Compact tabs with modern styling */}
      <Card
        style={{ 
          borderRadius: 8,
          border: '1px solid #e8e8e8',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          items={tabItems}
          size="small"
          tabBarStyle={{
            margin: 0,
            padding: '8px 16px 0 16px',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
          }}
          className="project-details-tabs"
        />
      </Card>

      {/* * Compact and modern tab styling */}
      <style>{`
        .project-details-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        
        .project-details-tabs .ant-tabs-tab {
          padding: 8px 16px !important;
          margin: 0 2px !important;
          border-radius: 6px 6px 0 0;
          font-weight: 500;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
          border: none !important;
        }
        
        .project-details-tabs .ant-tabs-tab:hover {
          background: rgba(24, 144, 255, 0.08);
          color: #1890ff;
        }
        
        .project-details-tabs .ant-tabs-tab-active {
          background: #ffffff !important;
          color: #1890ff !important;
          box-shadow: 0 -1px 4px rgba(0,0,0,0.06);
        }
        
        .project-details-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #1890ff;
          font-weight: 600;
        }
        
        .project-details-tabs .ant-tabs-ink-bar {
          height: 2px;
          background: #1890ff;
          border-radius: 2px 2px 0 0;
        }
        
        .project-details-tabs .ant-tabs-content-holder {
          padding: 20px;
          background: #ffffff;
        }
        
        .project-details-tabs .ant-tabs-content {
          height: 100%;
        }
        
        /* * Smooth tab transition */
        .project-details-tabs .ant-tabs-tabpane {
          animation: fadeIn 0.25s ease-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
          transform: translateY(0);
        }
      }
      `}</style>
    </div>
  )
}

export default ProjectDetailsPage

